/**
 * Connect your AI (slice 1b spec §6.2): a provider, a model from its list,
 * then a ChatGPT sign-in or an API key. `/setup` and Settings → AI (§6.3)
 * both render it. It reads the connection from `/api/me`, so a reload mid-
 * connect shows where it is.
 */
import { Button, buttonVariants } from "@crew/ui/components/button";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@crew/ui/components/field";
import { Input } from "@crew/ui/components/input";
import { Spinner } from "@crew/ui/components/spinner";
import { StatusDot } from "@crew/ui/components/status";
import { ArrowUpRight, Copy, Eye, EyeOff } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { errorHint } from "../../app/api";
import type { AiProvider, Me } from "../../app/me";
import { useCancelSignIn, useConnectAi, useModels } from "./api";
import { startedConnect, useNow, type Connect } from "./hooks";

export type { Connect } from "./hooks";
import { ModelPicker } from "./model-picker";
import { ProviderRadioGrid } from "./provider-tiles";
import {
  connectBody,
  connectErrors,
  connectPicksDefault,
  connectFailure,
  connectLabel,
  connectView,
  type StartedConnect,
  formModel,
  initialProvider,
  keptModel,
  modelGroups,
  modelLabel,
  providerModels,
  PROVIDERS,
  SIGN_IN_SITES,
  signInProvider,
  signsIn,
  workersAiAccountId,
  type CatalogModel,
  type SignInProvider,
} from "./onboarding";

export type KeyProvider = Exclude<AiProvider, SignInProvider | "claude">;

/** The launch providers, in the grid's order. */
const PROVIDER_ORDER = Object.keys(PROVIDERS) as AiProvider[];

/** Where each key provider's keys are made. */
const KEY_PAGES: Record<KeyProvider, { url: string; site: string }> = {
  anthropic: { url: "https://console.anthropic.com/settings/keys", site: "the Anthropic Console" },
  openai: { url: "https://platform.openai.com/api-keys", site: "the OpenAI Platform" },
  openrouter: { url: "https://openrouter.ai/settings/keys", site: "OpenRouter" },
  "workers-ai": { url: "https://dash.cloudflare.com/profile/api-tokens", site: "Cloudflare" },
  runware: { url: "https://my.runware.ai/keys", site: "Runware" },
};

/** "14:21": the minutes and seconds until `at`, never below 0:00. */
function timeLeft(at: string, now: number): string {
  const total = Math.max(0, Math.ceil((Date.parse(at) - now) / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

type Ai = NonNullable<Me["computer"]>["ai"];

/** A provider's models, grouped by generation, no prices (slice 1b spec §4.4). */
function ModelSelect({
  id,
  models,
  value,
  onChange,
  disabled,
}: {
  id: string;
  models: readonly CatalogModel[];
  value: string | undefined;
  onChange: (model: string) => void;
  disabled: boolean;
}) {
  return (
    <ModelPicker
      id={id}
      label="Model"
      disabled={disabled}
      groups={modelGroups(models)}
      value={value ?? null}
      onChange={onChange}
      recommend
    />
  );
}

function CopyCode({ code, tone = "light" }: { code: string; tone?: "light" | "dark" }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="ghost"
      size="sm"
      className={
        tone === "dark"
          ? "bg-background/10 text-background hover:bg-background/20 hover:text-background"
          : "bg-foreground/5"
      }
      onClick={() =>
        void navigator.clipboard.writeText(code).then(
          () => setCopied(true),
          () => setCopied(false),
        )
      }
    >
      <Copy aria-hidden />
      {copied ? "Copied" : "Copy"}
    </Button>
  );
}

/** Cancels the sign-in in progress; `label` is the button's. */
export function CancelSignIn({
  label = "Cancel",
  variant = "ghost",
}: {
  label?: string;
  variant?: "ghost" | "outline";
}) {
  const cancel = useCancelSignIn();
  return (
    <div className="flex flex-col gap-2">
      <Button
        variant={variant}
        className="self-start"
        disabled={cancel.isPending}
        onClick={() => cancel.mutate()}
      >
        {cancel.isPending ? "Cancelling…" : label}
      </Button>
      <ErrorLine error={cancel.error} />
    </div>
  );
}

/**
 * The device code to type in ChatGPT (or at xAI, for Grok), with Copy, and
 * the link there (§6.2); until the computer has one, that it's coming.
 */
export function SignInCode({
  provider,
  pending,
}: {
  provider: SignInProvider;
  pending: Ai["pending"];
}) {
  const { site, note } = SIGN_IN_SITES[provider];
  return (
    <div className="flex flex-col gap-4 rounded-lg bg-foreground/3 p-5 ring-1 ring-border ring-inset">
      <div className="flex flex-col gap-1">
        <p className="text-sm leading-5 font-semibold text-foreground">
          Sign in to {site} and enter this code
        </p>
        <p className="text-meta leading-[19px] text-muted-foreground">{note}</p>
      </div>
      {pending ? (
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex h-13 items-center gap-3.5 rounded-md bg-card pr-2 pl-4.5 ring-1 ring-input">
            <code
              aria-label="Sign-in code"
              className="font-mono text-[26px] leading-8 font-medium tracking-[0.12em] text-foreground select-all"
            >
              {pending.code}
            </code>
            <CopyCode code={pending.code} />
          </div>
          <a
            href={pending.url}
            target="_blank"
            rel="noreferrer"
            className={buttonVariants({ className: "h-13 px-4.5" })}
          >
            Open {site}
            <ArrowUpRight aria-hidden />
          </a>
        </div>
      ) : (
        <p className="flex h-13 items-center gap-2 text-sm text-copy">
          <Spinner className="size-4" />
          Getting a sign-in code from your computer…
        </p>
      )}
    </div>
  );
}

/** The wait for the owner to approve, with how long the code has left. */
export function SignInWaiting({
  provider,
  pending,
}: {
  provider: SignInProvider;
  pending: NonNullable<Ai["pending"]>;
}) {
  const now = useNow();
  return (
    <p className="flex items-center gap-2.5 text-sm leading-5 text-copy">
      <Spinner className="size-4" />
      <span className="grow">Waiting for you to approve in {SIGN_IN_SITES[provider].site}…</span>
      <span className="text-xs text-hint tabular-nums">
        Code expires in {timeLeft(pending.expires_at, now)}
      </span>
    </p>
  );
}

/**
 * The device code, the link, and the wait for the owner to approve (§6.2).
 * Cancel comes with the code: until it shows there's nothing to cancel
 * (`DELETE /api/computer/ai/pending` is `409` then).
 */
function SignIn({ provider, pending }: { provider: SignInProvider; pending: Ai["pending"] }) {
  return (
    <div className="flex flex-col gap-4" data-connect-state={pending ? "code" : "signing-in"}>
      <SignInCode provider={provider} pending={pending} />
      {pending ? (
        <>
          <SignInWaiting provider={provider} pending={pending} />
          <CancelSignIn />
        </>
      ) : null}
    </div>
  );
}

function Connecting() {
  return (
    <div className="flex flex-col gap-1.5" data-connect-state="connecting">
      <p className="flex items-center gap-2 text-body text-foreground">
        <Spinner className="size-4" />
        Connecting…
      </p>
      <p className="text-meta text-muted-foreground">
        Waking your computer, saving the connection there, and starting your Chief of Staff on it.
        About a minute.
      </p>
    </div>
  );
}

/** The key field while hidden or shown: its type, and the eye's label and icon. */
const EYE = {
  hidden: { type: "password", label: "Show the key", Icon: Eye },
  shown: { type: "text", label: "Hide the key", Icon: EyeOff },
};

/** The key, hidden unless shown with the eye. */
function KeyInput({
  value,
  onChange,
  invalid,
  placeholder,
}: {
  value: string;
  onChange: (key: string) => void;
  invalid: boolean;
  placeholder?: string;
}) {
  const [shown, setShown] = useState(false);
  const eye = shown ? EYE.shown : EYE.hidden;
  return (
    <div className="relative">
      <Input
        id="api-key"
        type={eye.type}
        autoComplete="off"
        spellCheck={false}
        required
        aria-invalid={invalid || undefined}
        placeholder={placeholder}
        className="h-10 pr-10 font-mono tracking-[0.02em]"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <button
        type="button"
        onClick={() => setShown(!shown)}
        aria-label={eye.label}
        className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-hint hover:text-foreground"
      >
        <eye.Icon className="size-4" />
      </button>
    </div>
  );
}

export function ApiKeyField({
  provider,
  value,
  onChange,
  error,
}: {
  provider: KeyProvider;
  value: string;
  onChange: (key: string) => void;
  error: string | null;
}) {
  const page = KEY_PAGES[provider];
  const name = PROVIDERS[provider].name;
  return (
    <Field data-invalid={error ? true : undefined}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <FieldLabel htmlFor="api-key">{name} API key</FieldLabel>
        <a
          href={page.url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-meta text-foreground underline decoration-foreground/30 underline-offset-[3px] hover:decoration-foreground"
        >
          Get a key from {page.site}
          <ArrowUpRight className="size-3" aria-hidden />
        </a>
      </div>
      <KeyInput value={value} onChange={onChange} invalid={Boolean(error)} />
      {error ? <FieldError>{error}</FieldError> : null}
      <FieldDescription>
        Crew checks the key with {name}, then keeps it only on your computer.
      </FieldDescription>
    </Field>
  );
}

/** How an owner makes a setup token without installing Claude Code first. */
const SETUP_COMMAND = "npx @anthropic-ai/claude-code setup-token";

/**
 * A Claude subscription (slice 1g spec §3.2): the owner makes the setup
 * token on their own computer, then pastes it, with the account's email so
 * the account is recognisable (the token doesn't say whose it is).
 */
export function ClaudeTokenFields({
  token,
  onToken,
  email,
  onEmail,
  error,
}: {
  token: string;
  onToken: (token: string) => void;
  email: string;
  onEmail: (email: string) => void;
  error: string | null;
}) {
  return (
    <div className="flex flex-col gap-5" data-claude-token>
      <Field>
        <FieldLabel>1 · Make a setup token on your computer</FieldLabel>
        <FieldDescription>
          Run this in a terminal (it needs Node), sign in with the Claude account you want your crew
          to use, and it prints a token that lasts a year.
        </FieldDescription>
        <div className="flex min-h-11 items-center justify-between gap-3 rounded-md bg-foreground py-1.5 pr-1.5 pl-4">
          <code className="font-mono text-sm break-all text-background">{SETUP_COMMAND}</code>
          <CopyCode code={SETUP_COMMAND} tone="dark" />
        </div>
      </Field>
      <Field data-invalid={error ? true : undefined}>
        <FieldLabel htmlFor="api-key">2 · Paste the setup token</FieldLabel>
        <KeyInput
          value={token}
          onChange={onToken}
          invalid={Boolean(error)}
          placeholder="sk-ant-oat01-…"
        />
        {error ? <FieldError>{error}</FieldError> : null}
        <FieldDescription>
          Your crew runs on it through Claude Code, within your plan's limits: it never turns on
          Extra Usage. Crew keeps the token only on your computer, and tries it with one short
          reply.
        </FieldDescription>
      </Field>
      <Field>
        <FieldLabel htmlFor="claude-email">
          3 · Account email <span className="font-normal text-muted-foreground">(optional)</span>
        </FieldLabel>
        <Input
          id="claude-email"
          type="email"
          autoComplete="off"
          placeholder="you@example.com"
          className="h-10"
          value={email}
          onChange={(e) => onEmail(e.target.value)}
        />
        <FieldDescription>
          The token doesn't say which Claude account made it. Add its email so you can tell it
          apart.
        </FieldDescription>
      </Field>
    </div>
  );
}

/**
 * Workers AI (slice 1h spec §8): the Cloudflare account ID, which is in the
 * address OpenClaw calls, and an API token with Workers AI on it. A Replace
 * starts from the account's current ID.
 */
export function WorkersAiFields({
  accountId,
  onAccountId,
  token,
  onToken,
  error,
}: {
  accountId: string;
  onAccountId: (id: string) => void;
  token: string;
  onToken: (token: string) => void;
  error: string | null;
}) {
  const page = KEY_PAGES["workers-ai"];
  const invalid = error ? true : undefined;
  return (
    <div className="flex flex-col gap-5" data-workers-ai>
      <Field data-invalid={invalid}>
        <FieldLabel htmlFor="cloudflare-account">Account ID</FieldLabel>
        <Input
          id="cloudflare-account"
          autoComplete="off"
          spellCheck={false}
          required
          inputMode="text"
          placeholder="32 characters"
          className="h-10 font-mono tracking-[0.02em]"
          aria-invalid={invalid}
          value={accountId}
          onChange={(e) => onAccountId(e.target.value)}
        />
        <FieldDescription>
          On your account's home page in the Cloudflare dashboard: Account ID, on the right.
        </FieldDescription>
      </Field>
      <Field data-invalid={invalid}>
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <FieldLabel htmlFor="api-key">API token</FieldLabel>
          <a
            href={page.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-meta text-foreground underline decoration-foreground/30 underline-offset-[3px] hover:decoration-foreground"
          >
            Create one in {page.site}
            <ArrowUpRight className="size-3" aria-hidden />
          </a>
        </div>
        <KeyInput value={token} onChange={onToken} invalid={Boolean(invalid)} />
        <FieldError>{error}</FieldError>
        <FieldDescription>
          Give it the Workers AI permission (Read is enough), for this account only. Don't use your
          Global API Key. Crew checks it with Cloudflare, then keeps it only on your computer.
        </FieldDescription>
      </Field>
    </div>
  );
}

/** A failed request's hint, or nothing. */
function ErrorLine({ error }: { error: unknown }) {
  return error ? <FieldError>{errorHint(error)}</FieldError> : null;
}

/** A connect that failed: why, above the form (whose button says Try again). */
function FailedNote({ failure }: { failure: string | null }) {
  if (!failure) return null;
  return (
    <p
      role="alert"
      className="flex items-start gap-2 rounded-lg bg-destructive/10 px-3 py-2 text-sm text-brick"
    >
      <StatusDot tone="error" className="mt-1.5" />
      {failure}
    </p>
  );
}

/** The form's provider: the fixed one, else a connect this panel started comes back on its own. */
function startingProvider(ai: Ai, fixed: AiProvider | undefined, started?: StartedConnect) {
  return initialProvider(ai, fixed ?? started?.provider);
}

/**
 * The model, when the connect picks the default (the first connection's
 * does); otherwise a line saying the default stays as it is.
 */
export function ModelField({
  provider,
  onChange,
  value,
  show,
  kept = null,
  disabled = false,
}: {
  provider: AiProvider;
  value: string | undefined;
  onChange: (model: string) => void;
  show: boolean;
  /** The workspace's default model, which a later connect keeps. */
  kept?: string | null;
  disabled?: boolean;
}) {
  const models = useModels();
  if (!show) {
    return kept ? (
      <FieldDescription data-kept-default>
        Connecting doesn't change your default model, {modelLabel(models.data, kept)}.
      </FieldDescription>
    ) : null;
  }
  return (
    <Field data-disabled={disabled || undefined}>
      <FieldLabel htmlFor="model">Model</FieldLabel>
      <ModelSelect
        id="model"
        models={providerModels(models.data, provider)}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
      <ErrorLine error={models.error} />
      <FieldDescription>You can change it later in Settings.</FieldDescription>
    </Field>
  );
}

/** What a provider's form takes besides the model: the key, and Claude's or Workers AI's extra field. */
type Credential = { apiKey: string; email: string; accountId: string };

/** The provider's credential fields: none for a sign-in. */
function CredentialFields({
  provider,
  value,
  onChange,
  error,
}: {
  provider: AiProvider;
  value: Credential;
  onChange: (change: Partial<Credential>) => void;
  error: string | null;
}) {
  const onToken = (apiKey: string) => onChange({ apiKey });
  if (provider === "claude") {
    return (
      <ClaudeTokenFields
        token={value.apiKey}
        onToken={onToken}
        email={value.email}
        onEmail={(email) => onChange({ email })}
        error={error}
      />
    );
  }
  if (provider === "workers-ai") {
    return (
      <WorkersAiFields
        accountId={value.accountId}
        onAccountId={(accountId) => onChange({ accountId })}
        token={value.apiKey}
        onToken={onToken}
        error={error}
      />
    );
  }
  if (signsIn(provider)) return null;
  return <ApiKeyField provider={provider} value={value.apiKey} onChange={onToken} error={error} />;
}

/** One provider's model, and its key or the sign-in button. */
function ProviderFields({
  provider,
  kept,
  connect,
  retrying,
  account,
  askModel,
  defaultModel,
  currentAccountId,
}: {
  provider: AiProvider;
  /** The workspace's model, when this provider is the one it's on (Reconnect keeps it). */
  kept: string | null;
  /** The workspace's default model, which a connect that doesn't pick one keeps. */
  defaultModel: string | null;
  connect: Connect;
  retrying: boolean;
  /** The account a Reconnect replaces the credential of; none adds one. */
  account?: string;
  /** Whether the connect picks the default model: only the first connection's does (slice 1c spec §7). */
  askModel: boolean;
  /** The Workers AI account's Cloudflare account ID, when it has one (a Replace starts from it). */
  currentAccountId?: string;
}) {
  const models = useModels();
  const [picked, setPicked] = useState<string>();
  const [credential, setCredential] = useState<Credential>({
    apiKey: "",
    email: "",
    accountId: currentAccountId ?? "",
  });
  const change = (next: Partial<Credential>) => setCredential((c) => ({ ...c, ...next }));
  const model = formModel(askModel, picked, providerModels(models.data, provider), kept);
  const errors = connectErrors(connect.error);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const { apiKey, email, accountId } = credential;
    const body = connectBody(provider, model, apiKey, account, email, accountId);
    connect.mutate(body, { onSuccess: () => change({ apiKey: "" }) });
  };
  return (
    <form onSubmit={submit} data-connect-state="form">
      <FieldGroup>
        <ModelField
          show={askModel}
          kept={account ? null : defaultModel}
          provider={provider}
          value={model}
          onChange={setPicked}
        />
        <CredentialFields
          provider={provider}
          value={credential}
          onChange={change}
          error={errors.key}
        />
        {errors.other ? <FieldError>{errors.other}</FieldError> : null}
        <Button type="submit" className="self-start" disabled={connect.isPending}>
          {connectLabel(provider, { pending: connect.isPending, retrying })}
        </Button>
      </FieldGroup>
    </form>
  );
}

/** The form: a failure's reason, the providers (unless fixed), then that provider's fields. */
function ConnectForm({
  ai,
  fixed,
  account,
  connect,
  askModel,
}: {
  ai: Ai;
  fixed?: AiProvider;
  account?: string;
  connect: Connect;
  askModel: boolean;
}) {
  const started = startedConnect(connect);
  const [chosen, setProvider] = useState(startingProvider(ai, fixed, started));
  // A fixed provider (Settings' tile or card) wins over what was chosen: the
  // panel isn't remounted when another tile is opened.
  const provider = fixed ?? chosen;
  const failure = connectFailure(ai, started);
  const choose = (p: AiProvider) => {
    setProvider(p);
    connect.reset();
  };
  return (
    <div className="flex flex-col gap-5">
      <FailedNote failure={failure} />
      {fixed ? null : (
        <ProviderRadioGrid
          providers={PROVIDER_ORDER.filter((p) => !ai.providers_off?.includes(p))}
          value={provider}
          onChange={choose}
        />
      )}
      {provider ? (
        <ProviderFields
          key={provider}
          provider={provider}
          kept={keptModel(ai, provider)}
          connect={connect}
          retrying={failure !== null}
          account={account}
          askModel={askModel}
          defaultModel={ai.model}
          currentAccountId={workersAiAccountId(ai)}
        />
      ) : null}
    </div>
  );
}

/**
 * Connect your AI: the form, or where a connect is (a sign-in's code,
 * "Connecting…"). `provider` fixes the provider (Settings' Add account,
 * Replace, Reconnect); `onConnected` runs once a connect started here has
 * finished. Settings passes its own `connect`, so the connect it started is
 * followed wherever the panel shows (the new provider's card, once it has one).
 */
export function ConnectPanel({
  ai,
  provider,
  account,
  connect: shared,
  askModel = connectPicksDefault(ai),
  onConnected = () => {},
}: {
  ai: Ai;
  provider?: AiProvider;
  /** The account a Replace or Reconnect replaces the credential of. */
  account?: string;
  connect?: Connect;
  /** Whether the form asks for a model: only the first connection picks the default. */
  askModel?: boolean;
  onConnected?: () => void;
}) {
  const own = useConnectAi();
  const connect = shared ?? own;
  const view = connectView(ai, startedConnect(connect));

  // A connect started here has finished (calling it again is harmless).
  useEffect(() => {
    if (view === "done") onConnected();
  }, [view, onConnected]);

  if (view === "sign-in") {
    return (
      <SignIn
        provider={signInProvider(ai, startedConnect(connect), provider)}
        pending={ai.pending}
      />
    );
  }
  if (view === "connecting") return <Connecting />;
  return (
    <ConnectForm ai={ai} fixed={provider} account={account} connect={connect} askModel={askModel} />
  );
}
